import { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileDown, FileSpreadsheet, Printer, Users, UserMinus, AlertTriangle, Sun, BookOpen } from "lucide-react";
import { ExcelExportLabel, excelExportButtonA11y } from "@/components/excelExportLabel";
import {
  NoTranslateText,
  PASTORAL_CHART_AUSENTES_DOMINGO,
  PASTORAL_SUB_NO_ASISTIO_CELULA,
  PASTORAL_SUB_NO_ASISTIO_DOMINGO,
} from "@/lib/reportColumnLabels";

/** Tonos más oscuros para mejor contraste en impresión / PDF */
const COL_BAR_TOTAL = "#64748b";
const COL_CELL = "#d97706";
const COL_SUN = "#7c3aed";
const COL_BOTH = "#be123c";
const COL_ACTIVE = "#059669";

function splitCellLabel(text, maxPerLine = 16) {
  const t = (text || "").trim() || "—";
  if (t.length <= maxPerLine) return [t];
  const space = t.lastIndexOf(" ", maxPerLine);
  const cut = space > 8 ? space : maxPerLine;
  const line1 = t.slice(0, cut).trim();
  const rest = t.slice(cut).trim();
  if (rest.length <= maxPerLine) return [line1, rest];
  return [line1, `${rest.slice(0, maxPerLine - 1)}…`];
}

function StatMini({ icon: Icon, label, value, sub, className = "" }) {
  return (
    <div
      className={`flex flex-col gap-1 rounded-xl border border-slate-300 bg-white px-3 py-3 shadow-sm print:border-slate-400 ${className}`}
    >
      <div className="flex items-center gap-2 text-slate-700">
        {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden />}
        <NoTranslateText className="text-[11px] font-medium uppercase tracking-wide">{label}</NoTranslateText>
      </div>
      <div className="text-2xl font-bold tabular-nums text-slate-900">{value}</div>
      {sub ? (
        <div className="text-xs text-slate-600">
          <NoTranslateText>{sub}</NoTranslateText>
        </div>
      ) : null}
    </div>
  );
}

/**
 * @param {object} props
 * @param {object | null | undefined} props.statistics - `absence_statistics` del API (solo reporte Ausentes)
 * @param {boolean} props.chartsExporting
 * @param {() => void} props.onExportChartsExcel
 * @param {() => void} props.onExportChartsPdf
 * @param {() => void} props.onPrintCharts
 */
export function PastoralAbsenceChartsBlock({
  statistics,
  chartsExporting,
  onExportChartsExcel,
  onExportChartsPdf,
  onPrintCharts,
}) {
  const [headerLogoVisible, setHeaderLogoVisible] = useState(true);
  const g = statistics?.grand_totals;

  const barData = useMemo(() => {
    if (!statistics?.by_cell?.length) return [];
    return statistics.by_cell.map((c) => {
      const full = (c.cell_name || "").trim() || "—";
      const lines = splitCellLabel(full, 15);
      return {
        cellLabel: lines.join("\n"),
        fullName: full,
        total: c.total_members,
        ausentesCelula: c.absent_cell_total,
        ausentesDomingo: c.absent_sunday_total,
        ausentesAmbas: c.absent_both,
      };
    });
  }, [statistics]);

  const donutData = useMemo(() => {
    if (!g) return [];
    return [
      { name: "Activos (cél. y dom.)", value: g.attended_both, fill: COL_ACTIVE },
      { name: "Faltó solo célula", value: g.absent_only_cell, fill: COL_CELL },
      { name: "Faltó solo domingo", value: g.absent_only_sunday, fill: COL_SUN },
      { name: "Faltó célula y domingo", value: g.absent_both, fill: COL_BOTH },
    ];
  }, [g]);

  const donutSum = useMemo(() => donutData.reduce((a, d) => a + d.value, 0), [donutData]);

  const priorityData = useMemo(() => {
    if (!g) return [];
    return [
      { name: "Atención urgente (ambas)", value: g.absent_both, fill: COL_BOTH },
      { name: "Seguimiento célula", value: g.absent_only_cell, fill: COL_CELL },
      { name: "Seguimiento domingo", value: g.absent_only_sunday, fill: COL_SUN },
      { name: "Activos (cél. y dom.)", value: g.attended_both, fill: COL_ACTIVE },
    ];
  }, [g]);

  if (!statistics || !g) return null;

  const pct = g.attendance_general_pct ?? 0;
  const reportDateStr =
    typeof statistics.meeting_date === "string" && statistics.meeting_date
      ? statistics.meeting_date
      : "—";
  const scopeStr = statistics.cell_scope_label || "Todas las células";
  const generatedStr = new Date().toLocaleString("es-MX", {
    dateStyle: "medium",
    timeStyle: "short",
  });

  const barTick = ({ x, y, payload }) => {
    const lines = String(payload?.value || "").split("\n");
    return (
      <g transform={`translate(${x},${y})`}>
        <text textAnchor="end" fill="#334155" fontSize={10}>
          {lines.map((line, i) => (
            <tspan key={i} x={0} dy={i === 0 ? 0 : 12}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  };

  return (
    <div
      id="pastoral-absence-charts-print"
      className="notranslate space-y-6 rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50/80 to-white p-4 shadow-sm print:border-gray-400 print:bg-white print:shadow-none sm:p-6"
      translate="no"
      lang="es"
    >
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between print:hidden">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Estadísticas de ausencias</h3>
          <p className="mt-1 text-sm text-slate-600">
            Visualización para líderes e iglesia · {statistics.meeting_date} · {statistics.cell_scope_label}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={!!chartsExporting}
            onClick={onExportChartsExcel}
            title="Exportar gráficas a Excel"
            aria-label="Exportar gráficas a Excel"
          >
            <FileSpreadsheet className="h-4 w-4 shrink-0" aria-hidden />
            <ExcelExportLabel busy={chartsExporting === "charts-excel"} />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={!!chartsExporting}
            onClick={onExportChartsPdf}
          >
            <FileDown className="h-4 w-4" />
            {chartsExporting === "charts-pdf" ? "Generando…" : "Exportar gráficas a PDF"}
          </Button>
          <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={onPrintCharts}>
            <Printer className="h-4 w-4" />
            Imprimir gráficas
          </Button>
        </div>
      </div>

      <div id="pastoral-charts-pdf-capture" className="space-y-10 print:space-y-8">
        <section
          data-pdf-section
          className="rounded-2xl border border-slate-300 bg-white px-8 py-10 shadow-sm print:border-slate-400"
        >
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-between sm:text-left">
            {headerLogoVisible && (
              <img
                src={`${import.meta.env.BASE_URL}icjeme-logo.png`}
                alt="ICJEME"
                className="h-12 w-auto max-w-[200px] object-contain"
                onError={() => setHeaderLogoVisible(false)}
              />
            )}
            <div className="min-w-0 flex-1 sm:pl-4">
              {!headerLogoVisible && (
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">ICJEME</p>
              )}
              <h2 className="mt-2 text-center text-2xl font-bold text-slate-900 sm:text-left">
                Estadísticas de ausencias pastorales
              </h2>
              <p className="mt-3 text-base text-slate-700">
                <span className="font-medium text-slate-800">Fecha de reunión:</span> {reportDateStr}
              </p>
              <p className="mt-1 text-base text-slate-700">
                <span className="font-medium text-slate-800">Alcance:</span> {scopeStr}
              </p>
              <p className="mt-2 text-sm text-slate-500">Reporte generado: {generatedStr}</p>
            </div>
          </div>
        </section>

        <section
          data-pdf-section
          className="rounded-2xl border border-slate-300 bg-slate-50/60 p-6 shadow-sm print:border-slate-400 print:bg-white"
        >
          <h3 className="mb-4 text-center text-sm font-semibold uppercase tracking-wide text-slate-600">
            Resumen numérico
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <StatMini icon={Users} label="Total miembros" value={g.total_members} />
            <StatMini
              icon={UserMinus}
              label="Total ausentes"
              value={g.total_unified_absent}
              sub="Célula o domingo (sin duplicar)"
            />
            <StatMini
              icon={BookOpen}
              label="Ausentes célula"
              value={g.absent_cell_total}
              sub={PASTORAL_SUB_NO_ASISTIO_CELULA}
            />
            <StatMini
              icon={Sun}
              label={PASTORAL_CHART_AUSENTES_DOMINGO}
              value={g.absent_sunday_total}
              sub={PASTORAL_SUB_NO_ASISTIO_DOMINGO}
            />
            <StatMini icon={AlertTriangle} label="Ausentes ambas" value={g.absent_both} />
            <StatMini icon={Users} label="Asistencia general" value={`${pct}%`} sub="Célula y domingo" />
          </div>
        </section>

        <section data-pdf-section className="rounded-2xl border border-slate-300 bg-white p-6 shadow-sm print:border-slate-400">
          <Card className="border-0 shadow-none print:break-inside-avoid">
            <CardHeader className="pb-3 pt-0">
              <CardTitle className="text-lg text-slate-900">Por célula</CardTitle>
              <CardDescription className="text-slate-600">
                Total miembros y ausencias por categoría (leyenda arriba)
              </CardDescription>
            </CardHeader>
            <CardContent className="h-[380px] w-full min-w-0 px-2 pb-4 pt-0 sm:h-[400px]">
              {barData.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={barData}
                    barCategoryGap="18%"
                    barGap={2}
                    margin={{ top: 52, right: 24, left: 20, bottom: 28 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" />
                    <XAxis
                      dataKey="cellLabel"
                      type="category"
                      tick={barTick}
                      interval={0}
                      height={56}
                      axisLine={{ stroke: "#64748b" }}
                      tickLine={{ stroke: "#64748b" }}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: "#334155" }}
                      width={44}
                      axisLine={{ stroke: "#64748b" }}
                      tickLine={{ stroke: "#64748b" }}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        borderColor: "#cbd5e1",
                        boxShadow: "0 4px 12px rgba(15,23,42,0.08)",
                      }}
                      formatter={(v, name) => [v, name]}
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName ?? ""}
                    />
                    <Legend
                      verticalAlign="top"
                      align="center"
                      wrapperStyle={{ fontSize: 11, color: "#334155", top: 0 }}
                    />
                    <Bar dataKey="total" name="Total miembros" fill={COL_BAR_TOTAL} radius={[2, 2, 0, 0]} />
                    <Bar dataKey="ausentesCelula" name="Ausentes célula" fill={COL_CELL} radius={[2, 2, 0, 0]} />
                    <Bar dataKey="ausentesDomingo" name={PASTORAL_CHART_AUSENTES_DOMINGO} fill={COL_SUN} radius={[2, 2, 0, 0]} />
                    <Bar dataKey="ausentesAmbas" name="Ausentes ambas" fill={COL_BOTH} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="py-16 text-center text-sm text-slate-500">No hay células en el alcance seleccionado.</p>
              )}
            </CardContent>
          </Card>
        </section>

        <section data-pdf-section className="rounded-2xl border border-slate-300 bg-white p-6 shadow-sm print:border-slate-400">
          <Card className="border-0 shadow-none print:break-inside-avoid">
            <CardHeader className="pb-3 pt-0">
              <CardTitle className="text-lg text-slate-900">Distribución</CardTitle>
              <CardDescription className="text-slate-600">Activos y tipos de ausencia (disjuntos)</CardDescription>
            </CardHeader>
            <CardContent className="h-[380px] w-full min-w-0 px-2 pb-2 pt-0 sm:h-[400px]">
              {donutSum > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 8, right: 8, left: 8, bottom: 56 }}>
                    <Tooltip
                      formatter={(value, _name, item) => [`${value} — ${item?.payload?.name ?? ""}`, ""]}
                      contentStyle={{ borderRadius: 8, borderColor: "#cbd5e1" }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      align="center"
                      wrapperStyle={{ fontSize: 11, color: "#334155", paddingTop: 8 }}
                    />
                    <Pie
                      data={donutData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="46%"
                      innerRadius={62}
                      outerRadius={100}
                      paddingAngle={2}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: "#64748b" }}
                    >
                      {donutData.map((entry, i) => (
                        <Cell key={`cell-${i}`} fill={entry.fill} stroke="#fff" strokeWidth={1.5} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="py-16 text-center text-sm text-slate-500">Sin datos para mostrar.</p>
              )}
            </CardContent>
          </Card>
        </section>

        <section data-pdf-section className="rounded-2xl border border-slate-300 bg-white p-6 shadow-sm print:border-slate-400">
          <Card className="border-0 shadow-none print:break-inside-avoid">
            <CardHeader className="pb-3 pt-0">
              <CardTitle className="text-lg text-slate-900">Prioridad pastoral</CardTitle>
              <CardDescription className="text-slate-600">Orden sugerido de seguimiento</CardDescription>
            </CardHeader>
            <CardContent className="h-[340px] w-full min-w-0 px-2 pb-4 pt-0 sm:h-[360px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={priorityData}
                  barCategoryGap="22%"
                  barGap={4}
                  margin={{ top: 16, right: 32, left: 8, bottom: 16 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" horizontal={false} />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: "#334155" }}
                    axisLine={{ stroke: "#64748b" }}
                    tickLine={{ stroke: "#64748b" }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={220}
                    tick={{ fontSize: 11, fill: "#334155" }}
                    interval={0}
                    axisLine={{ stroke: "#64748b" }}
                    tickLine={{ stroke: "#64748b" }}
                  />
                  <Tooltip
                    formatter={(v) => [`${v} persona(s)`, "Cantidad"]}
                    contentStyle={{ borderRadius: 8, borderColor: "#cbd5e1" }}
                  />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={26} minPointSize={2}>
                    {priorityData.map((entry, i) => (
                      <Cell key={`p-${i}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
